import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import type { GallerySortName } from '@sorare/core/src/contexts/config';
import type { GQLCtx } from '@sorare/core/src/contexts/graphql-search';
import { keyFacetsByApiField } from '@sorare/core/src/contexts/graphql-search';
import { BACKEND_CARD_SEARCH_FILTERS } from '@sorare/core/src/contexts/graphql-search/backendCardSearchFilters.ts';
import { pickRenderedBackendFilters } from '@sorare/core/src/contexts/graphql-search/pickRenderedBackendFilters.ts';
import { useGQLSearchState } from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { useInMemorySearchParams } from '@sorare/core/src/contexts/graphql-search/useInMemorySearchParams.ts';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards/index.tsx';
import { useCompetitionEligibilityFilterValue } from '@sorare/core/src/contexts/searchCards/useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from '@sorare/core/src/contexts/searchCards/useFavoriteFilterValue.ts';
import { useNotInLineupFilterValue } from '@sorare/core/src/contexts/searchCards/useNotInLineupFilterValue.ts';
import { useWatchlistFilterValue } from '@sorare/core/src/contexts/searchCards/useWatchlistFilterValue.ts';
import { useDeck } from '@sorare/core/src/hooks/decks/useDeck';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  getUserCardFilters,
  joinFiltersWithAnd,
  joinFiltersWithOr,
  sportFilter,
  toFilterLiteral,
  tokenHitFragment,
} from '@sorare/core/src/lib/algolia.ts';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';

import { toSortInputs } from '#src/hooks/search/useCardsSearchClient.ts';
import { USER_CARDS_SEARCH_FACET_VALUES_QUERY } from '#src/hooks/search/useUserCardsSearchClient.ts';

import type {
  TradeCardsSearchQuery,
  TradeCardsSearchQueryVariables,
} from './__generated__/useBackendTradeCardsSearch.graphql.ts';

const TRADE_CARDS_SEARCH_QUERY = gql`
  query TradeCardsSearchQuery(
    $userSlug: String!
    $sport: Sport!
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
  ) {
    user(slug: $userSlug) {
      slug
      searchCards(
        query: $query
        page: $page
        pageSize: $pageSize
        sorts: $sorts
        sport: $sport
        advancedFilters: $advancedFilters
        refinements: $refinements
        facets: $facets
      ) {
        page
        nbPages
        nbHits
        hits {
          slug
          ...Algolia_CardHit_anyCard
        }
        facets {
          field
          values {
            value
            count
          }
          min {
            value
          }
          max {
            value
          }
        }
      }
    }
  }
  ${tokenHitFragment}
` as TypedDocumentNode<TradeCardsSearchQuery, TradeCardsSearchQueryVariables>;

export const TRADE_SORTS: GallerySortName[] = [
  'Cards On Sale Newly Listed',
  'Cards On Sale Best Value',
  'Cards Highest Average Score',
];

const PAGE_SIZE = 15;

type Params = {
  sport: Sport;
  userSlug: string;
  cardFilters: FiltersGroup[];
};

export const useBackendTradeCardsSearch = ({
  sport,
  userSlug,
  cardFilters,
}: Params) => {
  const activeBackendCardFilters = useMemo(
    () => pickRenderedBackendFilters(BACKEND_CARD_SEARCH_FILTERS, cardFilters),
    [cardFilters]
  );

  const { ctxBase, facets, searchParams } = useGQLSearchState(
    activeBackendCardFilters,
    useInMemorySearchParams()
  );
  const { query, page, activeFacets, refinements, setSearchParams } = ctxBase;

  const currentSortName = (searchParams.get('s') ??
    TRADE_SORTS[0]) as GallerySortName;

  const { filters: searchCardsFilters } = useSearchCardsContext();

  const notInLineupActive = !!activeFacets.nl?.length;
  const { notInLineupFilterValue, loading: notInLineupLoading } =
    useNotInLineupFilterValue({ skip: !notInLineupActive });

  const favoriteActive = !!activeFacets.ff?.length;
  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteActive });

  const competitionEligibility = activeFacets.lf?.[0];
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(competitionEligibility);

  const watchlistId = activeFacets.wl?.[0];
  const { watchlistFilterValue, loading: watchlistLoading } =
    useWatchlistFilterValue({ filterValue: watchlistId || undefined });

  const filtersLoading = notInLineupLoading || watchlistLoading;

  const deckName = activeFacets.deck?.[0];
  const { deck } = useDeck({ sport, name: deckName, skip: !deckName });
  const deckTokens = deck?.cards.nodes;
  const deckFilterValue = deckName
    ? joinFiltersWithOr([
        ...(deckTokens?.map(({ slug }) => `slug:"${toFilterLiteral(slug)}"`) ||
          []),
        `custom_decks.name:"${toFilterLiteral(deckName)}"`,
      ])
    : undefined;

  const advancedFilters = joinFiltersWithAnd(
    [
      getUserCardFilters(undefined),
      ...searchCardsFilters,
      sportFilter(sport),
      notInLineupActive && notInLineupFilterValue,
      favoriteActive && favoriteFilterValue,
      competitionEligibility && competitionEligibilityFilterValue,
      watchlistId && watchlistFilterValue,
      deckFilterValue,
    ].filter(Boolean) as string[]
  );

  const { data, loading, previousData } = useQuery(TRADE_CARDS_SEARCH_QUERY, {
    skip: !userSlug || filtersLoading,
    variables: {
      userSlug,
      sport,
      query,
      page,
      pageSize: PAGE_SIZE,
      advancedFilters,
      sorts: toSortInputs(currentSortName, sport),
      refinements,
      facets,
    },
  });

  const [search] = useLazyQuery(USER_CARDS_SEARCH_FACET_VALUES_QUERY, {
    variables: {
      userSlug,
      sport,
      query,
      advancedFilters,
      refinements,
      field: '',
      facetQuery: '',
    },
  });

  const safeData = data ?? previousData;
  const searchCards = safeData?.user?.searchCards;

  const gqlSearchContextValue: GQLCtx = {
    ...ctxBase,
    search,
    extractFacetValues: searchFacetData =>
      searchFacetData?.user?.searchCardFacetValues,
    pages: searchCards?.nbPages ?? 0,
    nbHits: searchCards?.nbHits ?? 0,
    advancedFilters,
    onSaleOnly: false,
    sorts: [currentSortName],
    facets: keyFacetsByApiField(searchCards?.facets ?? []),
  };

  return {
    gqlSearchContextValue,
    searchCards,
    loading: loading || filtersLoading,
    page,
    setSearchParams,
  };
};
